import { api } from '../api/client'
import type { CompareChannel, ParameterKind, Series, SeriesPoint, SeriesResponse } from '../api/types'
import { MISSING, SENTINEL } from './quality'
import type { ExpressionMode } from './monitoringExpressions'
import {
  calculatedSensorStore, calculatedSourceId, calculationSourceBindings, compileFormula, isRawSourceId,
  validateCalculatedSensor, type CalculatedDefinition, type CalculationSourceBinding,
} from './calculatedSensors'
import {
  isManualSourceId, loadManualReadings, manualSensorSeries, manualSensorStore, manualSourceId, readManualSensors,
  type ManualSensor,
} from './manualSensors'

export interface FormulaProvenance {
  formula: string
  inputs: CalculationSourceBinding[]
  mode?: ExpressionMode
  alignment: 'exact-timestamp'
}
export interface AnalysisSource {
  id: string
  station: string
  station_name: string
  parameter: string
  display_name: string
  unit: string
  axis_group: string
  kind: ParameterKind
  precision: number | null
  type: 'raw' | 'calculated' | 'manual'
  provenance?: FormulaProvenance
  /** The gateway node or logger that measures it, for multi-sensor series
   *  (lib/workbooks.ts) to tell a station's sensors apart. */
  sensor?: string | null
  /** Silent between events by design (a tipping bucket): no freshness verdict. */
  event_driven?: boolean
}
export interface AnalysisSeries extends Series { provenance?: FormulaProvenance }

export function analysisSources(channels: CompareChannel[], definitions: CalculatedDefinition[],
    manual: ManualSensor[] = readManualSensors()): AnalysisSource[] {
  const raw = channels.map(channel => ({ id: channel.station + '|' + channel.parameter,
    station: channel.station, station_name: channel.station_name, parameter: channel.parameter,
    display_name: channel.display_name, unit: channel.unit, axis_group: channel.axis_group,
    kind: channel.kind, precision: channel.precision, type: 'raw' as const, sensor: channel.sensor, event_driven: channel.event_driven }))
  const calculated = definitions.filter(item => !validateCalculatedSensor(item)).map(item => ({
    id: calculatedSourceId(item.id), station: 'calculated', station_name: 'Calculated sensors',
    parameter: item.id, display_name: item.name, unit: item.unit, axis_group: calculatedAxisGroup(item),
    kind: 'instant' as const, precision: item.precision, type: 'calculated' as const,
    provenance: provenance(item),
  }))
  // A manual sensor sits under its station, beside the station's own
  // channels, when it has one; its id keeps it apart from them.
  const stationNames = new Map(channels.map(channel => [channel.station, channel.station_name]))
  const entered = manual.map(item => ({
    id: manualSourceId(item.id), station: item.station ?? 'manual',
    station_name: (item.station && (stationNames.get(item.station) ?? item.station_name)) || 'Manual sensors',
    parameter: item.id, display_name: item.name, unit: item.unit, axis_group: item.axis_group,
    kind: 'instant' as const, precision: item.precision, type: 'manual' as const,
  }))
  return [...raw, ...calculated, ...entered]
}
function provenance(item: CalculatedDefinition): FormulaProvenance {
  return { formula: item.formula, ...(item.mode ? { mode: item.mode } : {}),
    inputs: calculationSourceBindings(item).map(input => ({ ...input })), alignment: 'exact-timestamp' }
}
/** A declared output unit is not an automatic conversion. Keep derived
 * quantities separate from raw axes and from differently scaled outputs. */
function calculatedAxisGroup(item: CalculatedDefinition): string {
  return 'calculated:' + item.axis_group + ':' + item.unit
}

/** Keep fractional seconds beyond JavaScript's millisecond precision. A
 * numeric Date key would merge distinct sub-millisecond observations. */
function timestampKey(value: string): string | null {
  const match = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.(\d{1,9}))?(?:Z|[+-]\d{2}:\d{2})$/i.exec(value)
  const at = Date.parse(value)
  if (!match || !Number.isFinite(at)) return null
  return new Date(at).toISOString().slice(0, 19) + '.' + (match[1] ?? '').padEnd(9, '0') + 'Z'
}

/** Calculations are separate derived series. Raw points remain untouched.
 * Equivalent timezone representations match without rounding timestamps.
 * No nearest-neighbour join or fill is performed. */
export function calculateSensorSeries(definition: CalculatedDefinition, raw: Series[]): AnalysisSeries {
  const invalid = validateCalculatedSensor(definition)
  if (invalid) throw new Error(invalid)
  const formula = compileFormula(definition.formula, { mode: definition.mode })
  const sources = new Map(raw.map(series => [series.station + '|' + series.parameter, series]))
  const required = calculationSourceBindings(definition)
  const times = new Map<string, string>()
  const inputs = required.map(input => {
    const points = new Map<string, SeriesPoint>()
    for (const point of sources.get(input.sourceId)?.points ?? []) {
      const at = timestampKey(point[0])
      if (at === null) continue
      if (!times.has(at)) times.set(at, point[0])
      // Duplicate instants are ambiguous even when their source strings differ.
      // A gap is safer than arbitrarily selecting either observation.
      const prior = points.get(at)
      points.set(at, prior ? [point[0], null, (prior[2] ?? 0) | (point[2] ?? 0)] : point)
    }
    return { alias: input.alias, points }
  })
  const points: SeriesPoint[] = [...times.keys()].sort().map(at => {
    const values: Record<string, number | null> = {}
    let quality = 0
    for (const input of inputs) {
      const point = input.points.get(at)
      const flags = point?.[2] ?? 0
      // Missing and placeholder readings are not numeric inputs. Other
      // finite flagged readings may be real; retain them and all source QC.
      values[input.alias] = flags & (MISSING | SENTINEL) ? null : point?.[1] ?? null
      quality |= flags
    }
    return [times.get(at)!, formula.evaluate(values), quality]
  })
  return { station: 'calculated', parameter: definition.id, display_name: definition.name,
    unit: definition.unit, axis_group: calculatedAxisGroup(definition), kind: 'instant', points,
    provenance: provenance(definition) }
}

/** One raw request keeps the backend's common truncation cutoff meaningful
 * for both direct and calculated selections. Only referenced inputs are read.
 * Manual readings come from their own route (db/034), for the same window,
 * and are cut at the raw request's cutoff so every series covers one period.
 * The manual sensor list is waited for first: an analysis opened cold must
 * not call its manual sensor "no longer available" before the list has come. */
/** Whether a selection reads a manual sensor, itself or through a calculation. */
function readsManual(ids: string[], definitions: CalculatedDefinition[]): boolean {
  if (ids.some(isManualSourceId)) return true
  const chosen = new Set(ids)
  return definitions.some(definition => {
    if (!chosen.has(calculatedSourceId(definition.id))) return false
    try { return calculationSourceBindings(definition).some(binding => isManualSourceId(binding.sourceId)) }
    catch { return false } // an invalid formula is refused below, by name
  })
}

export async function fetchAnalysisSeries(ids: string[], from: Date, to: Date,
    definitions?: CalculatedDefinition[], manual?: ManualSensor[]): Promise<SeriesResponse> {
  if (!definitions) definitions = ids.some(id => id.startsWith('calculated|')) ? await calculatedSensorStore.ready() : []
  if (!manual) manual = readsManual(ids, definitions) ? await manualSensorStore.ready() : []
  if (!Number.isFinite(from.getTime()) || !Number.isFinite(to.getTime()) || from >= to) {
    throw new Error('Choose a valid time window.')
  }
  const selected = [...new Set(ids)]
  if (!selected.length) return { series: [], truncated: false, truncated_at: null }
  if (selected.length > 32) throw new Error('Choose at most 32 analysis sources.')
  const byId = new Map(definitions.map(item => [calculatedSourceId(item.id), item]))
  const manualById = new Map(manual.map(item => [manualSourceId(item.id), item]))
  const pairIds = new Set<string>(), manualIds = new Set<string>()
  const useManual = (id: string) => {
    const item = manualById.get(id)
    if (!item) throw new Error('A manual sensor in this analysis is no longer available.')
    manualIds.add(id)
  }
  for (const id of selected) {
    if (id.startsWith('calculated|')) {
      const item = byId.get(id)
      if (!item) throw new Error('A calculated sensor in this analysis is no longer available.')
      const invalid = validateCalculatedSensor(item)
      if (invalid) throw new Error(item.name + ': ' + invalid)
      calculationSourceBindings(item).forEach(input => isManualSourceId(input.sourceId) ? useManual(input.sourceId) : pairIds.add(input.sourceId))
    } else if (isManualSourceId(id)) {
      useManual(id)
    } else {
      if (!isRawSourceId(id)) throw new Error('Invalid analysis source.')
      pairIds.add(id)
    }
  }
  const pairs = [...pairIds].map(id => id.split('|') as [string, string])
  const [response, readings] = await Promise.all([
    pairs.length ? api.series({ stations: [], parameters: [], pairs, from, to, includeQuality: true })
      : Promise.resolve<SeriesResponse>({ series: [], truncated: false, truncated_at: null }),
    Promise.all([...manualIds].map(id => loadManualReadings(manualById.get(id)!.id, { from, to }))),
  ])
  const entered = [...manualIds].map((id, index) => manualSensorSeries({ ...manualById.get(id)!, readings: readings[index]! },
    from, to, response.truncated ? response.truncated_at : null))
  const inputs = [...response.series, ...entered]
  const raw = new Map(inputs.map(series => [series.station + '|' + series.parameter, series]))
  const series = selected.flatMap(id => {
    const definition = byId.get(id)
    if (definition) return [calculateSensorSeries(definition, inputs)]
    const item = raw.get(id)
    return item ? [item] : []
  })
  return { series, truncated: response.truncated, truncated_at: response.truncated_at }
}
